/**
 * Mapping helpers between the flat wizard form (CustomerAccountValues) and the
 * multi-company API shape (company.base + jurisdictions[market].data).
 *
 * Which keys belong to which market is owned by the FLAT_FORM_MARKETS registry
 * (markets.ts) — derived from the Zod sub-schemas, so this never drifts from the
 * form (and matches the backend 0033 backfill carve). Adding a flat-form country
 * is a registry entry; split/merge below stay generic.
 */
import type { CompanyDto } from './api';
import type { CompanyRow } from './components/CompaniesTable';
import { FLAT_FORM_MARKETS } from './markets';
import {
  COMPANY_TYPE_OPTIONS,
  COMPANY_TYPE_SHORT_LABELS,
  customerAccountDefaults,
  type CustomerAccountValues,
} from '@/schemas/customer-account';

const COMPANY_TYPE_LABELS = new Map(
  COMPANY_TYPE_OPTIONS.map((o) => [o.value as string, o.label]),
);

/** True if a value (recursively) holds any non-empty scalar. Empty strings,
 *  empty arrays, and objects/arrays whose leaves are all empty count as empty.
 *  Used to decide whether a market actually has data worth persisting. */
export function hasMeaningfulData(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.some(hasMeaningfulData);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasMeaningfulData);
  }
  return true;
}

/** Split the flat form into the company base + one blob per flat-form market,
 *  keyed by market code. Each market's keys (per FLAT_FORM_MARKETS) carve into
 *  its own jurisdiction[code].data; everything else is the base. Registry-driven
 *  — adding a market needs no change here. */
export function splitCompanyValues(values: Partial<CustomerAccountValues>): {
  base: Record<string, unknown>;
  markets: Record<string, Record<string, unknown>>;
} {
  const base: Record<string, unknown> = {};
  const markets: Record<string, Record<string, unknown>> = Object.fromEntries(
    FLAT_FORM_MARKETS.map((m) => [m.code, {} as Record<string, unknown>]),
  );
  for (const [key, value] of Object.entries(values)) {
    const owner = FLAT_FORM_MARKETS.find((m) => m.keys.has(key));
    if (owner) (markets[owner.code] ??= {})[key] = value;
    else base[key] = value;
  }
  return { base, markets };
}

/** Which flat-form market jurisdictions the wizard should upsert on save, given
 *  the split ``markets`` (from splitCompanyValues) and the markets the user has
 *  ADDED. Each added market in registry order, paired with its carved data — a
 *  market persists iff added (honest: a market exists iff added), so we never
 *  write a phantom jurisdiction for the empty fields a non-added market carries.
 *  Pure + registry-driven, so a new flat-form country is covered automatically. */
export function marketJurisdictionsToPersist(
  markets: Record<string, Record<string, unknown>>,
  addedMarkets: readonly string[],
): { marketCode: string; data: Record<string, unknown> }[] {
  return FLAT_FORM_MARKETS.filter((m) => addedMarkets.includes(m.code)).map((m) => ({
    marketCode: m.code,
    data: markets[m.code] ?? {},
  }));
}

/** One-time migration (on load): fold the legacy manufacturer captures
 *  (manufacturers[], manufacturing_entities[], manufacturer_of_record) into the
 *  consolidated facilities[] list, tagged by relationship, and backfill
 *  `relationship` on pre-existing facility rows. No dossier code ever read these
 *  keys, so this is a pure wizard-display migration; a mapped row that lacks a
 *  now-required field simply surfaces it for the user to complete. Idempotent —
 *  once re-saved, the old keys are gone and this is a no-op. */
function foldLegacyManufacturers(out: Record<string, unknown>): void {
  const asRows = (v: unknown): Record<string, unknown>[] =>
    Array.isArray(v) ? (v as Record<string, unknown>[]) : [];

  // Every row entering the form gets a stable `id` (see facilitySchema's doc
  // comment) — backfilled here for rows that never had one (pre-existing
  // server data, or the legacy captures folded in below) so the autosave
  // hold-back merge can key off row identity from the moment a company loads.
  const ownSites = asRows(out.facilities).map((f) => ({
    ...f,
    relationship: f.relationship ?? 'own_site',
    id: typeof f.id === 'string' && f.id ? f.id : crypto.randomUUID(),
  }));
  const contracted = asRows(out.manufacturers).map((m) => ({
    id: crypto.randomUUID(),
    relationship: 'contracted',
    name: m.name ?? '',
    legal_form: '',
    type: m.type ?? 'manufacturing',
    roles: m.roles ?? [],
    duns: '',
    address: m.address ?? '',
    city: m.city ?? '',
    state: m.state ?? '',
    postal: m.postal ?? '',
    country: m.country ?? '',
    phone: m.phone ?? '',
    contact: m.contact ?? '',
    gmp_status: m.gmp_status,
    is_manufacturer_of_record: false,
  }));
  const affiliated = asRows(out.manufacturing_entities).map((e) => ({
    id: crypto.randomUUID(),
    relationship: 'affiliated',
    name: e.name ?? '',
    legal_form: e.legal_form ?? '',
    type: 'manufacturing',
    roles: [],
    duns: '',
    address: e.address ?? '',
    city: '',
    state: '',
    postal: '',
    country: '',
    phone: '',
    contact: '',
    is_manufacturer_of_record: false,
  }));

  const merged: Record<string, unknown>[] = [...ownSites, ...contracted, ...affiliated];

  // Legacy manufacturer_of_record was a free-text name — flag the row it names.
  const mor =
    typeof out.manufacturer_of_record === 'string' ? out.manufacturer_of_record.trim() : '';
  if (mor) {
    const match = merged.find(
      (r) => typeof r.name === 'string' && r.name.trim() === mor,
    );
    if (match) match.is_manufacturer_of_record = true;
  }

  if (merged.length > 0) out.facilities = merged;
  delete out.manufacturers;
  delete out.manufacturing_entities;
  delete out.manufacturer_of_record;
}

/** Merge a company's base + each flat-form market's jurisdiction data back into
 *  flat form values. Markets are applied in registry order (last wins, matching
 *  the previous base→US→CA layering). Registry-driven — pass the company's
 *  jurisdiction rows and every flat-form market hydrates automatically. */
export function mergeCompanyValues(
  base: Record<string, unknown> | undefined,
  jurisdictions: readonly { market_code: string; data?: Record<string, unknown> | null }[] = [],
): CustomerAccountValues {
  const byCode = new Map(jurisdictions.map((j) => [j.market_code, j.data ?? {}]));
  let out: Record<string, unknown> = { ...customerAccountDefaults, ...(base ?? {}) };
  for (const m of FLAT_FORM_MARKETS) {
    out = { ...out, ...(byCode.get(m.code) ?? {}) };
  }
  foldLegacyManufacturers(out);
  return out as CustomerAccountValues;
}

/** Merge one autosave tick's `facilities` rows against the last-successfully-
 *  saved ones, holding back only rows the caller flags invalid via
 *  `rowIsInvalid(index)` — matched by each row's stable `id` (see
 *  facilitySchema), NOT array position. `useFieldArray.remove()` shifts later
 *  rows' indices down, so a positional lookup can substitute a DIFFERENT
 *  (already-removed) row's last-known-good value once an earlier row has been
 *  removed — silently resurrecting it and discarding whatever the actually-
 *  invalid row held. A row with no id match (never previously saved) is
 *  simply dropped, same as before. */
export function mergeFacilitiesField(
  previous: unknown,
  next: unknown,
  rowIsInvalid: (index: number) => boolean,
): CustomerAccountValues['facilities'] {
  const prevRows = Array.isArray(previous)
    ? (previous as CustomerAccountValues['facilities'])
    : [];
  const nextRows = Array.isArray(next) ? (next as CustomerAccountValues['facilities']) : [];
  const prevById = new Map(
    prevRows.filter((r) => r && typeof r.id === 'string' && r.id).map((r) => [r.id as string, r]),
  );
  return nextRows
    .map((row, i) => (rowIsInvalid(i) ? (row?.id ? prevById.get(row.id) : undefined) : row))
    .filter((row) => row !== undefined);
}

/** A company's display name — its legal name, or "Untitled company" while unset. */
export function companyName(dto: CompanyDto): string {
  const base = dto.base as Record<string, unknown>;
  return (
    (typeof base.legal_company_name === 'string' && base.legal_company_name.trim()) ||
    'Untitled company'
  );
}

/** Project an API company onto the presentational CompaniesTable row. */
export function companyDtoToRow(dto: CompanyDto): CompanyRow {
  const base = dto.base as Record<string, unknown>;
  const name = companyName(dto);
  const typeRaw =
    typeof base.company_type === 'string' ? base.company_type.trim() : '';
  const typeLabel = COMPANY_TYPE_LABELS.get(typeRaw) ?? (typeRaw || '—');
  // Chip text vs tooltip text: the table shows the short form, the title the
  // full one. An unknown/blank type falls through to the same string for both.
  const typeShort = COMPANY_TYPE_SHORT_LABELS[typeRaw] ?? typeLabel;
  const euData = dto.jurisdictions.find((j) => j.market_code === 'EU')?.data as
    | { member_states?: Record<string, unknown> }
    | undefined;
  return {
    id: dto.id,
    name,
    typeLabel,
    typeShort,
    // Oldest → newest: the API returns jurisdictions ordered by created_at, so
    // a company's markets read in the order they were added (US first, etc.).
    markets: dto.jurisdictions.map((j) => j.market_code),
    euStates: Object.keys(euData?.member_states ?? {}),
    status: dto.status === 'submitted' ? 'submitted' : 'draft',
    submittedAt: dto.submitted_at ?? '',
    parentId: dto.parent_company_id ?? undefined,
  };
}
